import React from "react";
import { Box, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TimelineOutcomeEnum } from "@/domain/troubleshooting.enum";
import { TimelineEvent } from "@/domain/troubleshooting.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import {
  TIMELINE_MARKER_SIZE_PX,
  worstOutcome,
} from "@/utils/troubleshooting.utils";
import { OutcomeIcon, outcomeColor, useOutcomeLabel } from "./outcome";
import { getEventTypeMeta } from "./eventType";

export const TIMELINE_TIME_FORMAT = "DD MMM YYYY, HH:mm:ss";
export const TIMELINE_CLOCK_FORMAT = "HH:mm:ss";

/** Red outer ring of alert types (crash, sign-in required, start blocked…). */
const ALERT_RING = "#C62828";
/** Ring of the selected marker; dark so it reads on every outcome colour. */
const SELECTED_RING = "#261F1F";
const FOCUS_RING = "#6253A2";
/** CSS `content` for the pseudo-element of the "new" pulse. */
const EMPTY_CONTENT = "''";

const showsOutcomeBadge = (outcome: TimelineOutcomeEnum) =>
  outcome === TimelineOutcomeEnum.FAILED ||
  outcome === TimelineOutcomeEnum.SKIPPED;

interface MarkerState {
  selected: boolean;
  focused: boolean;
  isNew: boolean;
}

/**
 * One visual language for every marker: a circle filled with the outcome
 * colour and a white icon inside; alert types get a red outer ring; failures
 * keep a small outcome badge so colour is never the only cue.
 */
const markerSx = (
  fill: string,
  alert: boolean,
  { selected, focused, isNew }: MarkerState,
): SxProps<Theme> => ({
  position: "absolute",
  top: 8,
  width: TIMELINE_MARKER_SIZE_PX,
  height: TIMELINE_MARKER_SIZE_PX,
  p: 0,
  m: 0,
  border: 0,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backgroundColor: fill,
  color: "#FFFFFF",
  fontFamily: "inherit",
  // A thin white halo keeps markers distinct on the tick grid; alert types
  // add a red ring around it.
  boxShadow: alert
    ? `0 0 0 2px #FFFFFF, 0 0 0 4px ${ALERT_RING}`
    : "0 0 0 2px #FFFFFF",
  outline: selected
    ? `3px solid ${SELECTED_RING}`
    : focused
      ? `2px dashed ${FOCUS_RING}`
      : "none",
  outlineOffset: alert ? 5 : 3,
  zIndex: selected ? 3 : focused ? 2 : 1,
  transition: "transform 120ms ease-out",
  "&:hover": { transform: "scale(1.15)" },
  "&:focus-visible": {
    outline: `3px solid ${SELECTED_RING}`,
    outlineOffset: alert ? 5 : 3,
  },
  ...(isNew && {
    // Live: events of the last refresh pulse once, then fade.
    "&::after": {
      content: EMPTY_CONTENT,
      position: "absolute",
      inset: -6,
      borderRadius: "50%",
      border: `2px solid ${FOCUS_RING}`,
      pointerEvents: "none",
      animation: "timelineNewEvent 6s ease-out forwards",
    },
    "@keyframes timelineNewEvent": {
      "0%": { opacity: 1, transform: "scale(1)" },
      "100%": { opacity: 0, transform: "scale(1.6)" },
    },
  }),
});

const OutcomeBadge: React.FC<{ outcome: TimelineOutcomeEnum }> = ({
  outcome,
}) => (
  <Box
    component="span"
    data-testid="marker-outcome-badge"
    sx={{
      position: "absolute",
      right: -5,
      bottom: -5,
      display: "flex",
      backgroundColor: "#FFFFFF",
      borderRadius: "50%",
      fontSize: 12,
    }}
  >
    <OutcomeIcon outcome={outcome} fontSize="inherit" />
  </Box>
);

const markerLeft = (pct: number) =>
  `calc(${pct}% - ${TIMELINE_MARKER_SIZE_PX / 2}px)`;

interface SingleMarkerProps extends MarkerState {
  event: TimelineEvent;
  pct: number;
  /** Center in px on the measured track (exposed as data-x). */
  x?: number;
  onSelect: (event: TimelineEvent) => void;
}

export const SingleMarker: React.FC<SingleMarkerProps> = ({
  event,
  pct,
  x,
  onSelect,
  ...state
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const outcomeLabel = useOutcomeLabel();
  const typeMeta = getEventTypeMeta(event);
  const typeLabel = typeMeta ? tt(`eventTypes.${typeMeta.labelKey}`) : "";
  const time = formatInBackofficeLondon(event.occurredAt, TIMELINE_TIME_FORMAT);
  const label = tt("timeline.eventLabel", {
    title: event.title,
    outcome: outcomeLabel(event.outcome),
    time,
  });
  const alert = !!typeMeta?.alert;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {event.title}
          </Typography>
          {typeLabel && (
            <Typography variant="caption" component="div">
              {typeLabel}
            </Typography>
          )}
          <Typography variant="caption" component="div">
            {time} · {outcomeLabel(event.outcome)}
          </Typography>
          {event.reason && (
            <Typography variant="caption" component="div">
              {event.reason}
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        component="button"
        type="button"
        aria-label={typeLabel ? `${typeLabel}: ${label}` : label}
        aria-pressed={state.selected}
        data-event-id={event.id}
        data-event-ids={event.id}
        data-outcome={event.outcome}
        data-event-type={event.type}
        data-alert={alert || undefined}
        data-focused={state.focused || undefined}
        data-new={state.isNew || undefined}
        data-selected={state.selected || undefined}
        data-x={x === undefined ? undefined : Math.round(x)}
        onClick={() => onSelect(event)}
        sx={{
          ...(markerSx(outcomeColor(event.outcome), alert, state) as object),
          left: markerLeft(pct),
        }}
      >
        {typeMeta ? (
          <>
            <typeMeta.Icon
              aria-hidden
              data-type-icon={typeMeta.labelKey}
              sx={{ fontSize: 14, color: "#FFFFFF" }}
            />
            {showsOutcomeBadge(event.outcome) && (
              <OutcomeBadge outcome={event.outcome} />
            )}
          </>
        ) : (
          <OutcomeIcon
            outcome={event.outcome}
            color="#FFFFFF"
            sx={{ fontSize: 14 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

interface ClusterMarkerProps extends MarkerState {
  events: TimelineEvent[];
  pct: number;
  /** Center in px on the measured track (exposed as data-x). */
  x?: number;
  open: boolean;
  onOpen: (anchor: HTMLElement) => void;
}

export const ClusterMarker: React.FC<ClusterMarkerProps> = ({
  events,
  pct,
  x,
  open,
  onOpen,
  ...state
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const outcomeLabel = useOutcomeLabel();
  const worst = worstOutcome(events);
  const hasAlert = events.some((e) => getEventTypeMeta(e)?.alert);
  const from = formatInBackofficeLondon(
    events[0].occurredAt,
    TIMELINE_CLOCK_FORMAT,
  );
  const to = formatInBackofficeLondon(
    events[events.length - 1].occurredAt,
    TIMELINE_CLOCK_FORMAT,
  );
  const label = tt("timeline.clusterLabel", {
    total: events.length,
    from,
    to,
    outcome: outcomeLabel(worst),
  });

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {tt("timeline.clusterTitle", { total: events.length })}
          </Typography>
          <Typography variant="caption" component="div">
            {from === to ? from : `${from} – ${to}`}
          </Typography>
          <Typography variant="caption" component="div">
            {t("timeline.clusterHint")}
          </Typography>
        </Box>
      }
    >
      <Box
        component="button"
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        data-cluster-size={events.length}
        data-event-ids={events.map((e) => e.id).join(" ")}
        data-outcome={worst}
        data-alert={hasAlert || undefined}
        data-focused={state.focused || undefined}
        data-new={state.isNew || undefined}
        data-selected={state.selected || undefined}
        data-x={x === undefined ? undefined : Math.round(x)}
        onClick={(e: React.MouseEvent<HTMLElement>) => onOpen(e.currentTarget)}
        sx={{
          ...(markerSx(outcomeColor(worst), false, state) as object),
          left: markerLeft(pct),
        }}
      >
        <Typography
          component="span"
          data-testid="cluster-count"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
            color: "#FFFFFF",
          }}
        >
          {events.length > 99 ? "99+" : events.length}
        </Typography>
        {hasAlert && (
          <Box
            component="span"
            data-testid="cluster-alert-dot"
            sx={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 9,
              height: 9,
              borderRadius: "50%",
              backgroundColor: ALERT_RING,
              border: "2px solid #FFFFFF",
            }}
          />
        )}
        {showsOutcomeBadge(worst) && <OutcomeBadge outcome={worst} />}
      </Box>
    </Tooltip>
  );
};
